import React, { useState, useEffect, useRef } from "react";
import { collection, query, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import * as XLSX from "xlsx";
import styles from './ReporteComprasVentas.module.css';
import logoEmpresa from '../images/logo-empresa.png';

const ReporteComprasVentas = ({ empresa, identificacion }) => {
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [facturas, setFacturas] = useState(null); 
  const [facturasAgrupadas, setFacturasAgrupadas] = useState({});
  const [totalesPorGrupo, setTotalesPorGrupo] = useState({});
  const [error, setError] = useState("");
  const [cargando, setCargando] = useState(false);
  const [logoBase64, setLogoBase64] = useState("");
  const [logoCargado, setLogoCargado] = useState(false);
  const [mostrarLogo] = useState(true);
  
  const [totales, setTotales] = useState({
    totalVentas: 0,
    totalCompras: 0,
    totalGastos: 0,
    diferencia: 0,
    totalImpuestoVentas: 0,
    totalImpuestoCompras: 0,
    totalImpuestoGastos: 0,
    diferenciaImpuestos: 0,
    totalNCVentas: 0,
    totalNCCompras: 0,
    totalNCGastos: 0,
    totalNDVentas: 0,
    totalNDCompras: 0,
    totalNDGastos: 0,
    totalTiquetesVentas: 0,
    totalTiquetesCompras: 0,
    totalTiquetesGastos: 0
  });

  const tableContainerRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  // Función para convertir imagen a Base64
  const convertirImagenABase64 = (img, maxWidth, maxHeight) => {
    const canvas = document.createElement('canvas');
    let width = img.width;
    let height = img.height;

    if (width > maxWidth) {
      height = (height * maxWidth) / width;
      width = maxWidth;
    }
    if (height > maxHeight) {
      width = (width * maxHeight) / height;
      height = maxHeight;
    }

    canvas.width = width;
    canvas.height = height;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, width, height);
    
    return canvas.toDataURL('image/png');
  };

  // Cargar el logo al montar el componente
  useEffect(() => {
    const cargarLogo = () => {
      const img = new Image();
      img.src = logoEmpresa;
      
      img.onload = () => {
        const base64 = convertirImagenABase64(img, 150, 150);
        setLogoBase64(base64);
        setLogoCargado(true);
      };
      
      img.onerror = () => {
        console.error("Error al cargar el logo");
        setLogoCargado(false);
      };
    };

    cargarLogo();
  }, []);

  useEffect(() => {
    const establecerFechaActual = () => {
      const hoy = new Date();
      const fechaFormateada = hoy.toISOString().split('T')[0];
      setFechaFin(fechaFormateada);
    };

    establecerFechaActual();
  }, []);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const handleMouseDown = (e) => {
      isDragging.current = true;
      startX.current = e.pageX - container.offsetLeft;
      scrollLeft.current = container.scrollLeft;
      container.style.cursor = 'grabbing';
      container.style.userSelect = 'none';
    };

    const handleMouseLeave = () => {
      isDragging.current = false;
      container.style.cursor = 'grab';
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      container.style.cursor = 'grab';
      container.style.userSelect = 'auto';
    };

    const handleMouseMove = (e) => {
      if (!isDragging.current) return;
      e.preventDefault();
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX.current) * 2;
      container.scrollLeft = scrollLeft.current - walk;
    };

    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mouseleave', handleMouseLeave);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mousemove', handleMouseMove);

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mouseleave', handleMouseLeave);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mousemove', handleMouseMove);
    };
  }, [facturas]);

  // Función para agrupar facturas por tipo
  const agruparFacturasPorTipo = (facturas) => {
  const grupos = {
    ventas: [],
    tiquetesVentas: [],
    ncVentas: [],
    ndVentas: [],
    compras: [],
    tiquetesCompras: [],
    ncCompras: [],
    ndCompras: [],
    gastos: [],
    tiquetesGastos: [],
    ncGastos: [],
    ndGastos: []
  };

   const totalesGrupo = {
    ventas: { totalConvertido: 0, impuestoConvertido: 0 },
    tiquetesVentas: { totalConvertido: 0, impuestoConvertido: 0 },
    ncVentas: { totalConvertido: 0, impuestoConvertido: 0 },
    ndVentas: { totalConvertido: 0, impuestoConvertido: 0 },
    compras: { totalConvertido: 0, impuestoConvertido: 0 },
    tiquetesCompras: { totalConvertido: 0, impuestoConvertido: 0 },
    ncCompras: { totalConvertido: 0, impuestoConvertido: 0 },
    ndCompras: { totalConvertido: 0, impuestoConvertido: 0 },
    gastos: { totalConvertido: 0, impuestoConvertido: 0 },
    tiquetesGastos: { totalConvertido: 0, impuestoConvertido: 0 },
    ncGastos: { totalConvertido: 0, impuestoConvertido: 0 },
    ndGastos: { totalConvertido: 0, impuestoConvertido: 0 }
  };

  facturas.forEach(factura => {
    const tipo = determinarTipoOperacion(factura);
    if (grupos[tipo]) {
      grupos[tipo].push(factura);
      
      const montoConvertido = calcularMontoConvertido(factura);
      const impuestoConvertido = calcularImpuestoConvertido(factura);
      
      totalesGrupo[tipo].totalConvertido += montoConvertido;
      totalesGrupo[tipo].impuestoConvertido += impuestoConvertido;
    }
  });

  // Ordenar cada grupo por fecha
  Object.keys(grupos).forEach(tipo => {    
    grupos[tipo].sort((a, b) => {
      // Primero, ordenar por nombre del emisor
      const emisorA = a.emisorNombre || '';
      const emisorB = b.emisorNombre || '';
      if (emisorA.localeCompare(emisorB) !== 0) {
        return emisorA.localeCompare(emisorB);
      }
      // Si los emisores son iguales, ordenar por fecha de emisión
      return a.fechaEmision.localeCompare(b.fechaEmision);
    });
  });

  return { grupos, totalesGrupo };
};

  const formatoNumero = (numero, decimales = 2) => {
    if (numero === null || numero === undefined || isNaN(numero)) return 'N/A';
    
    const numeroFormateado = Number(numero).toFixed(decimales);
    const partes = numeroFormateado.split('.');
    partes[0] = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    return partes.join('.');
  };

  const formatoFecha = (fechaStr) => {
    if (!fechaStr) return 'N/A';
    
    try {
      if (typeof fechaStr === 'string' && fechaStr.includes('-')) {
        const [anio, mes, dia] = fechaStr.split('T')[0].split('-');
        return `${dia}/${mes}/${anio}`;
      }
      
      const fecha = new Date(fechaStr);
      if (isNaN(fecha.getTime())) return 'N/A';
      
      const dia = fecha.getDate().toString().padStart(2, '0');
      const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
      const anio = fecha.getFullYear();
      return `${dia}/${mes}/${anio}`;
    } catch (error) {
      console.error('Error formateando fecha:', error);
      return 'N/A';
    }
  };

  const calcularMontoConvertido = (factura) => {
    const total = Number(factura.totalComprobante) || 0;
    const tipoCambio = Number(factura.tipoCambio) || 0;
    
    if (tipoCambio > 1) {
      return total * tipoCambio;
    }
    return total;
  };

  const calcularImpuestoConvertido = (factura) => {
    const totalImpuesto = Number(factura.totalImpuesto) || 0;
    const tipoCambio = Number(factura.tipoCambio) || 0;
    
    if (tipoCambio > 1) {
      return totalImpuesto * tipoCambio;
    }
    return totalImpuesto;
  };

  const determinarTipoOperacion = (factura) => {
    const emisorIdentificacion = factura.emisorNumeroIdentificacion || '';
    const esPropia = emisorIdentificacion === identificacion;
    const tipoDocumento = factura.tipoDocumento || 'FacturaElectronica';
    
    const tipoFactura = factura.tipoFactura || '';
    const tipoFacturaLower = tipoFactura.toLowerCase();
    const esGasto = tipoFacturaLower.includes('gasto') || 
                   tipoFacturaLower.includes('servicio') || 
                   tipoFacturaLower.includes('pago') ||
                   (factura.categoria && factura.categoria.toLowerCase() === 'gasto');

    if (tipoDocumento === 'TiqueteElectronico') {
      if (esPropia) return 'tiquetesVentas';
      return esGasto ? 'tiquetesGastos' : 'tiquetesCompras';
    }
    
    if (tipoDocumento === 'FacturaElectronica') {
      if (esPropia) return 'ventas';
      return esGasto ? 'gastos' : 'compras';
    }
    
    if (tipoDocumento === 'NotaCreditoElectronica') {
      if (esPropia) return 'ncVentas';
      return esGasto ? 'ncGastos' : 'ncCompras';
    }
    
    if (tipoDocumento === 'NotaDebitoElectronica') {
      if (esPropia) return 'ndVentas';
      return esGasto ? 'ndGastos' : 'ndCompras';
    }
    
    return esGasto ? 'gastos' : 'compras';
  };

  const calcularTotales = (facturas) => {
    let totalVentas = 0;
    let totalCompras = 0;
    let totalGastos = 0;
    let totalImpuestoVentas = 0;
    let totalImpuestoCompras = 0;
    let totalImpuestoGastos = 0;
    let totalNCVentas = 0;
    let totalNCCompras = 0;
    let totalNCGastos = 0;
    let totalNDVentas = 0;
    let totalNDCompras = 0;
    let totalNDGastos = 0;
    let totalTiquetesVentas = 0;
    let totalTiquetesCompras = 0;
    let totalTiquetesGastos = 0;

    facturas.forEach(factura => {
      const montoConvertido = calcularMontoConvertido(factura);
      const impuestoConvertido = calcularImpuestoConvertido(factura);
      const tipoOperacion = determinarTipoOperacion(factura);
      
      switch (tipoOperacion) {
        case 'ventas':
          totalVentas += montoConvertido;
          totalImpuestoVentas += impuestoConvertido;
          break;
        case 'tiquetesVentas':
          totalTiquetesVentas += montoConvertido;
          totalImpuestoVentas += impuestoConvertido;
          break;
        case 'compras':
          totalCompras += montoConvertido;
          totalImpuestoCompras += impuestoConvertido;
          break;
        case 'tiquetesCompras':
          totalTiquetesCompras += montoConvertido;
          totalImpuestoCompras += impuestoConvertido;
          break;
        case 'gastos':
          totalGastos += montoConvertido;
          totalImpuestoGastos += impuestoConvertido;
          break;
        case 'tiquetesGastos':
          totalTiquetesGastos += montoConvertido;
          totalImpuestoGastos += impuestoConvertido;
          break;
        case 'ncVentas':
          totalNCVentas += montoConvertido;
          totalImpuestoVentas -= impuestoConvertido;
          break;
        case 'ncCompras':
          totalNCCompras += montoConvertido;
          totalImpuestoCompras -= impuestoConvertido;
          break;
        case 'ncGastos':
          totalNCGastos += montoConvertido;
          totalImpuestoGastos -= impuestoConvertido;
          break;
        case 'ndVentas':
          totalNDVentas += montoConvertido;
          totalImpuestoVentas += impuestoConvertido;
          break;
        case 'ndCompras':
          totalNDCompras += montoConvertido;
          totalImpuestoCompras += impuestoConvertido;
          break;
        case 'ndGastos':
          totalNDGastos += montoConvertido;
          totalImpuestoGastos += impuestoConvertido;
          break;
        default:
          totalCompras += montoConvertido;
          totalImpuestoCompras += impuestoConvertido;
          break;
      }
    });

    const ventasNetas = totalVentas - totalNCVentas + totalNDVentas + totalTiquetesVentas;
    const comprasNetas = totalCompras - totalNCCompras + totalNDCompras + totalTiquetesCompras;
    const gastosNetos = totalGastos - totalNCGastos + totalNDGastos + totalTiquetesGastos;
    
    const diferencia = ventasNetas - comprasNetas - gastosNetos;
    const diferenciaImpuestos = totalImpuestoVentas - totalImpuestoCompras - totalImpuestoGastos;

    setTotales({
      totalVentas: ventasNetas,
      totalCompras: comprasNetas,
      totalGastos: gastosNetos,
      diferencia,
      totalImpuestoVentas,
      totalImpuestoCompras,
      totalImpuestoGastos,
      diferenciaImpuestos,
      totalNCVentas,
      totalNCCompras,
      totalNCGastos,
      totalNDVentas,
      totalNDCompras,
      totalNDGastos,
      totalTiquetesVentas,
      totalTiquetesCompras,
      totalTiquetesGastos
    });
  };

  const obtenerFacturasClasificadas = async () => {
    if (!fechaInicio || !fechaFin || !empresa) {
      setError("Seleccioná ambas fechas y asegurate de definir la empresa.");
      return;
    }

    setCargando(true);
    setFacturas(null);
    setFacturasAgrupadas({});
    setTotalesPorGrupo({});
    setError("");
    setTotales({ 
      totalVentas: 0, 
      totalCompras: 0, 
      totalGastos: 0,
      diferencia: 0,
      totalImpuestoVentas: 0,
      totalImpuestoCompras: 0,
      totalImpuestoGastos: 0,
      diferenciaImpuestos: 0,
      totalNCVentas: 0,
      totalNCCompras: 0,
      totalNCGastos: 0,
      totalNDVentas: 0,
      totalNDCompras: 0,
      totalNDGastos: 0,
      totalTiquetesVentas: 0,
      totalTiquetesCompras: 0,
      totalTiquetesGastos: 0
    });

    try {
      const facturasRef = collection(db, "EncabezadoFactura");
      const q = query(facturasRef);
      
      const snapshot = await getDocs(q);
      const resultados = [];

      const fechaInicioStr = fechaInicio;
      const fechaFinStr = fechaFin;

      snapshot.forEach(doc => {
        const data = doc.data();
        const fechaEmisionCompleta = data.fechaEmision;
        
        if (fechaEmisionCompleta && typeof fechaEmisionCompleta === 'string') {
          const fechaDocumentoStr = fechaEmisionCompleta.substring(0, 10);
          
          if (fechaDocumentoStr >= fechaInicioStr && fechaDocumentoStr <= fechaFinStr) {
            const tipoOperacion = determinarTipoOperacion(data);
            resultados.push({ ...data, tipo: tipoOperacion, id: doc.id });
          }
        }
      });

      resultados.sort((a, b) => a.fechaEmision.localeCompare(b.fechaEmision));
      
      setFacturas(resultados);
      
      const { grupos, totalesGrupo } = agruparFacturasPorTipo(resultados);
      setFacturasAgrupadas(grupos);
      setTotalesPorGrupo(totalesGrupo);
      
      calcularTotales(resultados);

    } catch (err) {
      console.error("Error al cargar facturas:", err);
      setError("Hubo un problema al cargar las facturas. Revisa la consola para más detalles.");
    } finally {
      setCargando(false);
    }
  };

  const obtenerTextoOperacion = (tipo) => {
    switch (tipo) {
      case 'ventas': return 'VENTA';
      case 'tiquetesVentas': return 'TIQUETE VENTA';
      case 'compras': return 'COMPRA';
      case 'tiquetesCompras': return 'TIQUETE COMPRA';
      case 'gastos': return 'GASTO';
      case 'tiquetesGastos': return 'TIQUETE GASTO';
      case 'ncVentas': return 'NC VENTA';
      case 'ncCompras': return 'NC COMPRA';
      case 'ncGastos': return 'NC GASTO';
      case 'ndVentas': return 'ND VENTA';
      case 'ndCompras': return 'ND COMPRA';
      case 'ndGastos': return 'ND GASTO';
      default: return tipo.toUpperCase();
    }
  };

  const obtenerClaseOperacion = (tipo) => {
    if (tipo.includes('nc')) return styles.nc;
    if (tipo.includes('nd')) return styles.nd;
    if (tipo.includes('tiquete')) return styles.tiquete;
    if (tipo.includes('ventas')) return styles.ventas;
    if (tipo.includes('compras')) return styles.compras;
    if (tipo.includes('gastos')) return styles.gastos;
    return '';
  };

  const exportarAExcel = () => {
    if (!facturas || facturas.length === 0) {
      setError("No hay datos para exportar.");
      return;
    }

    try {
      const datosParaExcel = facturas.map(factura => {
        const montoConvertido = calcularMontoConvertido(factura);
        const impuestoConvertido = calcularImpuestoConvertido(factura);
        const tipoCambio = Number(factura.tipoCambio) || 0;
        
        return {
          "Tipo": obtenerTextoOperacion(factura.tipo),
          "Número Consecutivo": factura.numeroConsecutivo,
          "Fecha de Emisión": formatoFecha(factura.fechaEmision),
          "Emisor": factura.emisorNombre,
          "Receptor": factura.receptorNombre || 'N/A',
          "Código Moneda": factura.codigoMoneda || 'N/A',
          "Tipo de Cambio": tipoCambio > 0 ? tipoCambio : 'N/A',
          "Total Comprobante": Number(factura.totalComprobante),
          "Total Convertido": montoConvertido,
          "Total Impuesto": Number(factura.totalImpuesto) || 0,
          "Impuesto Convertido": impuestoConvertido,
          "Tipo Documento": factura.tipoDocumento || 'N/A'
        };
      });

      datosParaExcel.push({});
      datosParaExcel.push({
        "Tipo": "TOTAL VENTAS",
        "Total Convertido": totales.totalVentas
      });
      datosParaExcel.push({
        "Tipo": "TOTAL COMPRAS",
        "Total Convertido": totales.totalCompras
      });
      datosParaExcel.push({
        "Tipo": "TOTAL GASTOS",
        "Total Convertido": totales.totalGastos
      });
      datosParaExcel.push({
        "Tipo": "DIFERENCIA",
        "Total Convertido": totales.diferencia
      });
      datosParaExcel.push({});
      datosParaExcel.push({
        "Tipo": "TOTAL IMPUESTO VENTAS",
        "Impuesto Convertido": totales.totalImpuestoVentas
      });
      datosParaExcel.push({
        "Tipo": "TOTAL IMPUESTO COMPRAS",
        "Impuesto Convertido": totales.totalImpuestoCompras
      });
      datosParaExcel.push({
        "Tipo": "TOTAL IMPUESTO GASTOS",
        "Impuesto Convertido": totales.totalImpuestoGastos
      });
      datosParaExcel.push({
        "Tipo": "DIFERENCIA IMPUESTOS",
        "Impuesto Convertido": totales.diferenciaImpuestos
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(datosParaExcel);
      
      const columnWidths = [
        { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 30 }, { wch: 30 },
        { wch: 15 }, { wch: 15 }, { wch: 18 }, { wch: 18 }, 
        { wch: 15 }, { wch: 18 }, { wch: 20 }
      ];
      ws['!cols'] = columnWidths;
      
      XLSX.utils.book_append_sheet(wb, ws, "Reporte Compras Ventas Gastos");
      
      const nombreArchivo = `Reporte_Compras_Ventas_Gastos_${fechaInicio}_a_${fechaFin}.xlsx`;
      
      XLSX.writeFile(wb, nombreArchivo);
      
    } catch (error) {
      console.error("Error al exportar a Excel:", error);
      setError("Hubo un problema al exportar a Excel.");
    }
  };

const handleImprimir = () => {
  if (!facturas || facturas.length === 0) {
    setError("No hay datos para imprimir.");
    return;
  }
  
  const ventanaImpresion = window.open('', '_blank');
  
    // Definir el orden de los grupos para la impresión (mismo orden que en pantalla)
  const gruposOrdenados = [
    'ventas', 'tiquetesVentas', 'ncVentas', 'ndVentas',
    'compras', 'tiquetesCompras', 'ncCompras', 'ndCompras',
    'gastos', 'tiquetesGastos', 'ncGastos', 'ndGastos'
  ];

  ventanaImpresion.document.write(`
    <html>
      <head>
        <title>Reporte de Compras, Ventas y Gastos</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .report-header { margin-bottom: 20px; border-bottom: 2px solid #ddd; padding-bottom: 15px; display: flex; align-items: center; }
          .logo-container { margin-right: 20px; }
          .logo { max-width: 150px; max-height: 150px; }
          .header-info { flex-grow: 1; font-size: 12px; text-align: right; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; font-size: 12px; }
          th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
          th { background-color: #f2f2f2; }
          .align-right { text-align: right; }
          .compras { color: red; }
          .ventas { color: green; }
          .gastos { color: blue; }
          .nc { color: purple; font-weight: bold; }
          .nd { color: orange; font-weight: bold; }
          .tiquete { color: #0066cc; font-weight: bold; }
          .grupo-header { background-color: #f0f0f0; font-weight: bold; text-align: center; }
          .grupo-total { font-weight: bold; background-color: #e8f4fc; }
          .totales-container { display: flex; flex-wrap: wrap; justify-content: space-between; margin-top: 20px; gap: 15px; }
          .total-section { flex: 1; min-width: 280px; background-color: #f9f9f9; padding: 15px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
          .total-item { display: flex; justify-content: space-between; margin: 8px 0; padding: 5px 0; border-bottom: 1px dashed #eee; }
          .total-item:last-child { border-bottom: none; font-weight: bold; }
          @media print {
            .no-print { display: none; }
            body { margin: 0; padding: 15px; }
            table { font-size: 10px; }
            .total-section { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="report-header">
          <div class="logo-container">
            ${logoCargado ? `<img src="${logoBase64}" class="logo" alt="Logo de la empresa">` : ''}
          </div>
          <div class="header-info">
            <h2>Reporte de Compras, Ventas y Gastos</h2>
            <p><strong>Empresa:</strong> ${empresa}</p>
            <p><strong>Identificación:</strong> ${identificacion}</p>
            <p><strong>Período:</strong> ${formatoFecha(fechaInicio)} - ${formatoFecha(fechaFin)}</p>
            <p><strong>Fecha de generación:</strong> ${formatoFecha(new Date().toISOString())}</p>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Número Consecutivo</th>
              <th>Fecha de Emisión</th>
              <th>Emisor</th>
              <th>Receptor</th>
              <th>Código Moneda</th>
              <th class="align-right">Tipo de Cambio</th>
              <th class="align-right">Total Comprobante</th>
              <th class="align-right">Total Convertido</th>
              <th class="align-right">Total Impuesto</th>
              <th class="align-right">Impuesto Convertido</th>
            </tr>
          </thead>
          <tbody>
            ${gruposOrdenados.map(tipo => {
              const facturasGrupo = facturasAgrupadas[tipo] || [];
              if (facturasGrupo.length === 0) return '';
              
              return `
                <tr class="grupo-header">
                  <td colspan="11">
                    ${obtenerTextoOperacion(tipo).toUpperCase()} (${facturasGrupo.length} facturas)
                  </td>
                </tr>
                ${facturasGrupo.map(factura => {
                  const montoConvertido = calcularMontoConvertido(factura);
                  const impuestoConvertido = calcularImpuestoConvertido(factura);
                  const tipoCambio = Number(factura.tipoCambio) || 0;
                  const totalImpuesto = Number(factura.totalImpuesto) || 0;
                  
                  const fechaEmisionFormateada = formatoFecha(factura.fechaEmision);
                  
                  let claseTipo = '';
                  if (factura.tipo.includes('nc')) claseTipo = 'nc';
                  else if (factura.tipo.includes('nd')) claseTipo = 'nd';
                  else if (factura.tipo.includes('tiquete')) claseTipo = 'tiquete';
                  else if (factura.tipo.includes('ventas')) claseTipo = 'ventas';
                  else if (factura.tipo.includes('compras')) claseTipo = 'compras';
                  else if (factura.tipo.includes('gastos')) claseTipo = 'gastos';
                  
                  return `
                    <tr>
                      <td class="${claseTipo}">${obtenerTextoOperacion(factura.tipo)}</td>
                      <td>${factura.numeroConsecutivo}</td>
                      <td>${fechaEmisionFormateada}</td>
                      <td>${factura.emisorNombre}</td>
                      <td>${factura.receptorNombre || 'N/A'}</td>
                      <td>${factura.codigoMoneda || 'N/A'}</td>
                      <td class="align-right">${tipoCambio > 0 ? formatoNumero(tipoCambio, 4) : 'N/A'}</td>
                      <td class="align-right">${formatoNumero(factura.totalComprobante)}</td>
                      <td class="align-right">${formatoNumero(montoConvertido)}${tipoCambio > 1 ? ' *' : ''}</td>
                      <td class="align-right">${formatoNumero(totalImpuesto)}</td>
                      <td class="align-right">${formatoNumero(impuestoConvertido)}${tipoCambio > 1 ? ' *' : ''}</td>
                    </tr>
                  `;
                }).join('')}
                <tr class="grupo-total">
                  <td colspan="7" style="text-align: right;">Total ${obtenerTextoOperacion(tipo)}:</td>
                  <td class="align-right"></td>
                  <td class="align-right">${formatoNumero(totalesPorGrupo[tipo]?.totalConvertido || 0)}</td>
                  <td class="align-right"></td>
                  <td class="align-right">${formatoNumero(totalesPorGrupo[tipo]?.impuestoConvertido || 0)}</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>

        <div class="totales-container">
          <div class="total-section">
            <h3>Totales de Comprobantes</h3>
            <div class="total-item">
              <span>Total de Ventas:</span>
              <span>${formatoNumero(totales.totalVentas)}</span>
            </div>
            <div class="total-item">
              <span>Total de Compras:</span>
              <span>${formatoNumero(totales.totalCompras)}</span>
            </div>
            <div class="total-item">
              <span>Total de Gastos:</span>
              <span>${formatoNumero(totales.totalGastos)}</span>
            </div>
            <div class="total-item">
              <span>Ventas - Compras - Gastos:</span>
              <span>${formatoNumero(totales.diferencia)}</span>
            </div>
          </div>

          <div class="total-section">
            <h3>Totales de Impuestos</h3>
            <div class="total-item">
              <span>Total Impuesto Ventas:</span>
              <span>${formatoNumero(totales.totalImpuestoVentas)}</span>
            </div>
            <div class="total-item">
              <span>Total Impuesto Compras:</span>
              <span>${formatoNumero(totales.totalImpuestoCompras)}</span>
            </div>
            <div class="total-item">
              <span>Total Impuesto Gastos:</span>
              <span>${formatoNumero(totales.totalImpuestoGastos)}</span>
            </div>
            <div class="total-item">
              <span>Impuesto Ventas - Compras - Gastos:</span>
              <span>${formatoNumero(totales.diferenciaImpuestos)}</span>
            </div>
          </div>
        </div>

        ${facturas.some(factura => Number(factura.tipoCambio) > 1) ? 
          '<div><p>* Montos convertidos usando el tipo de cambio</p></div>' : ''}
        
        <div class="no-print">
          <br>
          <button onclick="window.print()">Imprimir</button>
          <button onclick="window.close()">Cerrar</button>
        </div>
      </body>
    </html>
  `);
  
  ventanaImpresion.document.close();
};
  // Función para renderizar filas de facturas agrupadas
const renderFacturasAgrupadas = () => {
  const gruposOrdenados = [
    'ventas', 'tiquetesVentas', 'ncVentas', 'ndVentas',
    'compras', 'tiquetesCompras', 'ncCompras', 'ndCompras',
    'gastos', 'tiquetesGastos', 'ncGastos', 'ndGastos'
  ];

  return gruposOrdenados.map((tipo) => {
    const facturasGrupo = facturasAgrupadas[tipo] || [];
    if (facturasGrupo.length === 0) return null;
    
    return (
      <React.Fragment key={tipo}>
        <tr className={styles['grupo-header']}>
          <td colSpan="11" className={styles['grupo-titulo']}>
            {obtenerTextoOperacion(tipo).toUpperCase()} ({facturasGrupo.length} facturas)
          </td>
        </tr>
        {facturasGrupo.map((factura) => {
          const montoConvertido = calcularMontoConvertido(factura);
          const impuestoConvertido = calcularImpuestoConvertido(factura);
          const tipoCambio = Number(factura.tipoCambio) || 0;
          const totalImpuesto = Number(factura.totalImpuesto) || 0;
          
          return (
            <tr key={factura.id}>
              <td className={obtenerClaseOperacion(factura.tipo)}>
                {obtenerTextoOperacion(factura.tipo)}
              </td>
              <td>{factura.numeroConsecutivo}</td>
              <td>{formatoFecha(factura.fechaEmision)}</td>
              <td>{factura.emisorNombre}</td>
              <td>{factura.receptorNombre || 'N/A'}</td>
              <td>{factura.codigoMoneda || 'N/A'}</td>
              <td className={styles['align-right']}>
                {tipoCambio > 0 ? formatoNumero(tipoCambio, 4) : 'N/A'}
              </td>
              <td className={styles['align-right']}>
                {formatoNumero(factura.totalComprobante)}
              </td>
              <td className={`${styles['monto-convertido']} ${styles['align-right']}`}>
                {formatoNumero(montoConvertido)}
                {tipoCambio > 1 && <span className={styles['conversion-indicator']}> *</span>}
              </td>
              <td className={styles['align-right']}>
                {formatoNumero(totalImpuesto)}
              </td>
              <td className={`${styles['impuesto-convertido']} ${styles['align-right']}`}>
                {formatoNumero(impuestoConvertido)}
                {tipoCambio > 1 && <span className={styles['conversion-indicator']}> *</span>}
              </td>
            </tr>
          );
        })}
        <tr className={styles['grupo-total']}>
          <td colSpan="7" className={styles['grupo-total-label']}>
            Total {obtenerTextoOperacion(tipo)}:
          </td>
          <td className={styles['align-right']}></td>
          <td className={styles['align-right']}>
            {formatoNumero(totalesPorGrupo[tipo]?.totalConvertido || 0)}
          </td>
          <td className={styles['align-right']}></td>
          <td className={styles['align-right']}>
            {formatoNumero(totalesPorGrupo[tipo]?.impuestoConvertido || 0)}
          </td>
        </tr>
      </React.Fragment>
    );
  });
};
  return (
    <div className={styles['report-container-full']}>
      <div className={styles['header-with-logo']}>
        {mostrarLogo && logoCargado && (
          <div className={styles['logo-container']}>
            <img src={logoBase64} alt="Logo de la empresa" className={styles.logo} />
          </div>
        )}
        <div>
          <h2>📊 Reporte de Compras, Ventas y Gastos</h2>
          <p><strong>Empresa:</strong> {empresa}</p>
          <p><strong>Identificación:</strong> {identificacion}</p>
        </div>
      </div>

      <div className={styles['input-group']}>
        <label>Desde:</label>
        <input
          type="date"
          value={fechaInicio}
          onChange={(e) => setFechaInicio(e.target.value)}
          disabled={cargando}
        />

        <label>Hasta:</label>
        <input
          type="date"
          value={fechaFin}
          onChange={(e) => setFechaFin(e.target.value)}
          disabled={cargando}
        />

        <button onClick={obtenerFacturasClasificadas} disabled={cargando}>
          {cargando ? "Buscando..." : "Buscar"}
        </button>

        {facturas && facturas.length > 0 && (
          <>
            <button onClick={exportarAExcel} className={styles['export-button']}>
              Exportar a Excel
            </button>
            <button onClick={handleImprimir} className={styles['print-button']}>
              Imprimir
            </button>
          </>
        )}
      </div>

      {error && <p className={styles['error-message']}>{error}</p>}
      {cargando && <p>Cargando facturas...</p>}

      {facturas && facturas.length === 0 && !cargando && (
        <p>No se encontraron facturas en el rango seleccionado.</p>
      )}

      {facturas && facturas.length > 0 && (
        <>
          <div className={styles['table-container-full']} ref={tableContainerRef}>
            <table className={styles['factura-table-full']}>
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Número Consecutivo</th>
                  <th>Fecha de Emisión</th>
                  <th>Emisor</th>
                  <th>Receptor</th>
                  <th>Código Moneda</th>
                  <th className={styles['align-right']}>Tipo de Cambio</th>
                  <th className={styles['align-right']}>Total Comprobante</th>
                  <th className={styles['align-right']}>Total Convertido</th>
                  <th className={styles['align-right']}>Total Impuesto</th>
                  <th className={styles['align-right']}>Impuesto Convertido</th>
                </tr>
              </thead>
              <tbody>
                {renderFacturasAgrupadas()}
              </tbody>
            </table>
          </div>

          <div className={styles['totales-container']}>
            <div className={styles['total-section']}>
              <h3>Totales de Comprobantes</h3>
              <div className={styles['total-item']}>
                <span className={styles['total-label']}>Total de Ventas:</span>
                <span className={styles['total-value']}>{formatoNumero(totales.totalVentas)}</span>
              </div>
              <div className={styles['total-item']}>
                <span className={styles['total-label']}>Total de Compras:</span>
                <span className={styles['total-value']}>{formatoNumero(totales.totalCompras)}</span>
              </div>
              <div className={styles['total-item']}>
                <span className={styles['total-label']}>Total de Gastos:</span>
                <span className={styles['total-value']}>{formatoNumero(totales.totalGastos)}</span>
              </div>
              <div className={styles['total-item']}>
                <span className={styles['total-label']}>Ventas - Compras - Gastos:</span>
                <span className={styles['total-value']}>{formatoNumero(totales.diferencia)}</span>
              </div>
            </div>

            <div className={styles['total-section']}>
              <h3>Totales de Impuestos</h3>
              <div className={styles['total-item']}>
                <span className={styles['total-label']}>Total Impuesto Ventas:</span>
                <span className={styles['total-value']}>{formatoNumero(totales.totalImpuestoVentas)}</span>
              </div>
              <div className={styles['total-item']}>
                <span className={styles['total-label']}>Total Impuesto Compras:</span>
                <span className={styles['total-value']}>{formatoNumero(totales.totalImpuestoCompras)}</span>
              </div>
              <div className={styles['total-item']}>
                <span className={styles['total-label']}>Total Impuesto Gastos:</span>
                <span className={styles['total-value']}>{formatoNumero(totales.totalImpuestoGastos)}</span>
              </div>
              <div className={styles['total-item']}>
                <span className={styles['total-label']}>Impuesto Ventas - Compras - Gastos:</span>
                <span className={styles['total-value']}>{formatoNumero(totales.diferenciaImpuestos)}</span>
              </div>
            </div>
          </div>

          {facturas.some(factura => Number(factura.tipoCambio) > 1) && (
            <div className={styles['nota-conversion']}>
              <p>* Montos convertidos usando el tipo de cambio</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReporteComprasVentas;